import styled from '@emotion/styled';
import type { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { RacemapColors } from '@racemap/utilities/consts/common';
import {
  aggregateLoadProductCounts,
  getBasePriceOfEventType,
  getPricingRulesOfEvent,
  getProductsOfBillingCycles,
  getRegisteredCountProducts,
  getStripeProductOfBillableItemType,
  isBillableItem,
  isCountBillableItemType,
  isEventDayBasedBillableItem,
} from '@racemap/utilities/functions/billing';
import { getEventCyclesOfTimeRange } from '@racemap/utilities/functions/billing/getEventCyclesOfTimeRange';
import { getUserCreditsAccumulated } from '@racemap/utilities/functions/billing/getUserCreditsAccumulated';
import { getHumanReadableIntervalDuration } from '@racemap/utilities/functions/getHumanReadableInterval';
import { Card, Spin } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useGetProductPrice } from 'src/lib/customHooks/useGetProductPrice';
import { useEventLoads, useUser } from '../../../lib/customHooks';
import { formatNumber, formatPrice, getLabelForBillingItemType } from '../../../misc/pricing';
import type { CurrentEvent } from '../../../store/events/events_reducers';

interface Props {
  event: Immutable<CurrentEvent>;
  title?: string;
}

export const AlreadyPayedList: FC<Props> = ({ event, title }) => {
  const { user: creator } = useUser({ userId: event.creatorId });
  const getPrice = useGetProductPrice({ isReseller: creator?.isReseller });
  const { loads, isLoading } = useEventLoads(event.id);
  const basePrice = getBasePriceOfEventType(event.type);
  const stripeBasePrice = basePrice ? getStripeProductOfBillableItemType(basePrice) : null;

  if (creator == null) return null;

  const { startTime, endTime } = event;
  const billableEvents = event.billableItems.filter(isEventDayBasedBillableItem);
  const basePriceEvent = event.billableItems.find((e) => e.type === basePrice);
  const billedItems = event.billableItems.filter(isBillableItem);
  const pricing = getPricingRulesOfEvent(event);

  if (startTime == null || endTime == null || creator == null) return null;

  const billingCycles = getEventCyclesOfTimeRange(billableEvents, pricing.numDaysEventCycle);
  const billingCycleProducts = getProductsOfBillingCycles(
    billingCycles,
    basePriceEvent != null,
    event.type,
  );
  const countProducts = getRegisteredCountProducts(billedItems);
  const loadProducts = aggregateLoadProductCounts(loads);
  const productsPrepared = Object.entries({
    ...billingCycleProducts,
    ...countProducts,
    ...loadProducts,
  })
    .sort(([productA], [productB]) => productA.localeCompare(productB))
    .filter(([, amount]) => amount > 0) as Array<[StripeProducts, number]>;
  const userCreditsMap = getUserCreditsAccumulated(event.userCredits, true);

  const startTimeObj = billingCycles.eventCycles
    .reduce<null | DateTime>(
      (acc, cycle) =>
        DateTime.fromJSDate(cycle.startDate) < (acc || Number.POSITIVE_INFINITY)
          ? DateTime.fromJSDate(cycle.startDate)
          : null,
      null,
    )
    ?.toUTC()
    .startOf('day');
  const endTimeObj = billingCycles.eventCycles
    .reduce<null | DateTime>(
      (acc, cycle) =>
        DateTime.fromJSDate(cycle.endDate) > (acc || Number.NEGATIVE_INFINITY)
          ? DateTime.fromJSDate(cycle.endDate)
          : null,
      null,
    )
    ?.toUTC()
    .endOf('day');
  const duration =
    startTimeObj != null && endTimeObj != null ? endTimeObj.diff(startTimeObj) : null;

  if (basePriceEvent != null && stripeBasePrice != null) {
    productsPrepared.unshift([stripeBasePrice, 1]);
  }

  return (
    <Spin spinning={isLoading}>
      <Container>
        <h5>{title || 'Before extension'}:</h5>

        <table>
          <tbody>
            <tr>
              <td>
                <strong>Start:</strong>{' '}
                {startTimeObj?.toLocaleString(DateTime.DATETIME_FULL) || '--'}
              </td>
              <td>
                <strong>End:</strong> {endTimeObj?.toLocaleString(DateTime.DATETIME_FULL) || '--'}
              </td>
            </tr>
            <tr>
              <td>
                {duration != null ? getHumanReadableIntervalDuration(duration) : '--'} duration
              </td>
              <td>{billingCycles.eventCycles.length}x Event Cycles</td>
            </tr>
          </tbody>
        </table>

        {productsPrepared.length > 0 && (
          <>
            <section>Already charged usage from the event start until now:</section>

            <table>
              <thead>
                <tr>
                  <th className="labelColumn">Product</th>
                  <th className="unitColumn">Units</th>
                  <th>Price per Unit</th>
                  <th>Total Price</th>
                </tr>
              </thead>
              <tbody>
                {productsPrepared.map(([productKey, amount]) => (
                  <>
                    <tr key={productKey}>
                      <td className="labelColumn">{getLabelForBillingItemType(productKey)}</td>
                      <td className="unitColumn">{formatNumber(amount)}</td>
                      <td>
                        {!isCountBillableItemType(productKey) &&
                          formatPrice(getPrice?.(productKey, 1))}
                      </td>
                      <td>
                        {!isCountBillableItemType(productKey) &&
                          formatPrice(getPrice?.(productKey, amount))}
                      </td>
                    </tr>
                    {userCreditsMap.has(productKey) && (
                      <tr key={`${productKey}-credits`}>
                        <td className="labelColumn">
                          Credits applied for {getLabelForBillingItemType(productKey)}
                        </td>
                        <td className="unitColumn">
                          {formatNumber(userCreditsMap.get(productKey) || 0)}
                        </td>
                        <td />
                        <td>
                          -{' '}
                          {formatPrice(getPrice?.(productKey, userCreditsMap.get(productKey) || 0))}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Container>
    </Spin>
  );
};

const Container = styled(Card)`
    width: 100%;
    border-color: ${RacemapColors.DarkGray};
    background-color: ${RacemapColors.LightLightGray};

    .ant-card-body {
        padding: 8px 15px;
    }

    h5 {
        font-size: 1.1rem;
    }

    section {
        margin-bottom: 0.6rem;
    }

    table {
        width: 100%;
        margin-bottom: 0.6rem;

        th {
            font-weight: 600;
            border-bottom: 1px solid ${RacemapColors.Gray};
            width: 25%;
        }

        td {
          width: 25%;
        }

        .labelColumn {
          width: 40%;
        }

        .unitColumn {
          width: 10%;
          text-align: center;
        }
        
    }
`;
